import { ElementType, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  combineStoreScopes,
  defaultValueMap,
  Footer,
  formValueMap2knowledges,
  getStoreScopesReducedStoreIds,
  Inline,
  removeStoreScopesAll,
  stepKnowledgeTypeMap,
  stores2storeScopes,
  storeScopesIncludeStore,
  storeScopesRemoveStoreIds,
  StoreSelect,
  UnConfigStore,
  uniqStoreScopes,
} from './share';
import TransitDays, { areaRegions, regionAreasMap } from './TransitDays';
import ShipPlace from './ShipPlace';
import { DeleteOutlined, LoadingOutlined, PlusOutlined, SwapOutlined } from '@ant-design/icons';
import { Formik, FormikProps } from 'formik';
import { Button, Divider, message, Tabs, Tag } from 'antd';
import { useTranslate } from '@tolgee/react';
import useViewState, { ViewState } from './useViewState';
import toLowerCase from 'utils/toLowerCase';
import FormField from 'components/FormField';
import { MultipleShippingDaysFormValues, MultipleShippingDays } from './ShippingDays';
import FreeShipping from './FreeShipping';
import SpecifyExpressType from './SpecifyExpressType';
import CombinedShipping from './CombinedShipping';
import styled from '@emotion/styled';
import {
  getStoreGroupList,
  knowledgeRecreate,
  updateKnowledgeStatus,
  updateStoreGroupListBootGuide,
} from '@knowledge/service/api/general';
import { getStoreCount, StoreScopesPreview } from 'components/StoreScopes';
import isEqual from 'react-fast-compare';
import posthog from '@leyan/lytics';
import { stepTextMap } from 'views/BootGuide/components/RouterButton';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { get } from 'lodash-es';
import {
  filterStoreScopes,
  getRegionTypeStoresMap,
  getStoreMap,
  useGetKnowledgeValues,
  useInitialValues,
} from '@knowledge/knowledges/general/utils';
import { useRequest } from 'ahooks';
import {
  storeGroupList2withScopes,
  StoreGroupWithScopes,
  storeIds2stores,
  storeScopes2storeIds,
  useGetStoreGroupName,
} from '@knowledge/knowledges/general/StoreGroup/share';
import { Preview } from '@knowledge/Activity/share';
import { PreviewWrapper } from 'views/Assistant/Department/components/AssignReceptionConfColumn';
import StoreGroupModal from '@knowledge/knowledges/general/StoreGroup/StoreGroupModal';
import useModal from 'hooks/useModal';
import ScrollTo from 'views/BootGuide/components/ScrollTo';
import { getCommonTransferedKnowledges } from '@knowledge/knowledges/general/edit/hook';

type FV = {
  storeGroup: StoreGroupWithScopes;
  AI_MODEL_SHIPPING_DAYS: MultipleShippingDaysFormValues[];
  AI_MODEL_TRANSIT_DAYS: BootGuide.AiModel.FormValues.TransitDays[];
  AI_MODEL_SHIP_PLACE: BootGuide.AiModel.FormValues.ShipPlace[];
  AI_MODEL_FREE_SHIPPING: BootGuide.AiModel.FormValues.FreeShipping[];
  AI_MODEL_SPECIFY_EXPRESS_TYPE: BootGuide.AiModel.FormValues.SpecifyExpressType[];
  AI_MODEL_COMBINED_SHIPPING: BootGuide.AiModel.FormValues.CombinedShipping[];
}[];

const knowledgeTypes: Knowledge.KnowledgeType[] = [
  'LOGISTICS_SEND_OUT_TIME',
  'LOGISTICS_SEND_OUT_PLACE',
  'LOGISTICS_DELIVERED_IN_TIME',
  'SHOP_FREE_SHIPPING',
  'LOGISTICS_DELIVERY_COURIER_TYPE',
  'LOGISTICS_CONSOLIDATED_MULTIPLE_ORDER',
];

export const getShippingDaysDefaultValues = (
  storeType: 'local' | 'cross' | 'pf',
  storeScopes: General.StoreScope[],
  defaultValueRule?: BootGuide.AiModel.DefaultValueRule,
): MultipleShippingDaysFormValues => {
  const { AI_MODEL_SHIPPING_DAYS: shippingDaysDefaultValue } = defaultValueMap;
  const {
    TODAY_OR_TOMORROW_ON_TIME: {
      todayOrTomorrowTime = shippingDaysDefaultValue.todayOrTomorrowTime,
    } = {},
    RANGE: { crossStoreRange, localStoreRange, pfStoreRange } = {},
  } = defaultValueRule?.LOGISTICS_SEND_OUT_TIME || {};
  const storeRange = {
    pf: pfStoreRange,
    cross: crossStoreRange,
    local: localStoreRange,
  }[storeType];
  return !storeRange
    ? {
        first: {
          ...defaultValueMap.AI_MODEL_SHIPPING_DAYS,
          storeScopes,
        },
      }
    : storeRange.onSaleMin === storeRange.preSaleMin &&
      storeRange.onSaleMax === storeRange.preSaleMax
    ? {
        first: {
          ...defaultValueMap.AI_MODEL_SHIPPING_DAYS,
          storeScopes,
          valueType: 'range',
          saleType: 'all',
          todayOrTomorrowTime,
          range: {
            rangeMin: storeRange.onSaleMin,
            rangeMax: storeRange.onSaleMax,
            unit: 'DAY',
          },
        },
      }
    : {
        first: {
          ...defaultValueMap.AI_MODEL_SHIPPING_DAYS,
          storeScopes,
          valueType: 'range',
          saleType: 'inSale',
          todayOrTomorrowTime,
          range: {
            rangeMin: storeRange.onSaleMin,
            rangeMax: storeRange.onSaleMax,
            unit: 'DAY',
          },
        },
        preSale: {
          ...defaultValueMap.AI_MODEL_SHIPPING_DAYS,
          storeScopes,
          valueType: 'range',
          saleType: 'preSale',
          todayOrTomorrowTime,
          range: {
            rangeMin: storeRange.preSaleMin,
            rangeMax: storeRange.preSaleMax,
            unit: 'DAY',
          },
        },
      };
};

export const getTransitDaysDefaultValues = (
  region: General.UpperCaseRegion,
  storeType: 'local' | 'cross' | 'pf',
  storeScopes: General.StoreScope[],
  defaultValueRule?: BootGuide.AiModel.DefaultValueRule,
): BootGuide.AiModel.FormValues.TransitDays => {
  const { AI_MODEL_TRANSIT_DAYS: transitDaysDefaultValue } = defaultValueMap;
  const isAreaRegion = storeType !== 'cross' && areaRegions.includes(region);
  return defaultValueRule?.LOGISTICS_DELIVER
    ? isAreaRegion
      ? {
          ...transitDaysDefaultValue,
          storeScopes,
          valueType: 'area',
          areas:
            defaultValueRule.LOGISTICS_DELIVER.DESIGNATE_AREA.areaRanges
              .find(({ storeType }) => storeType === 'LOCAL')
              ?.areaRange.map((item) => {
                return {
                  areas: item.areas.filter((_) => regionAreasMap[region].includes(_)),
                  rangeMin: item.min,
                  rangeMax: item.max,
                };
              })
              .filter((_) => _.areas.length) || transitDaysDefaultValue.areas,
        }
      : {
          ...transitDaysDefaultValue,
          storeScopes,
          valueType: 'range',
          range:
            defaultValueRule.LOGISTICS_DELIVER.RANGE.transportRanges.reduce<
              undefined | { min: number; max: number }
            >((res, cur) => {
              if (
                cur.regions.includes(region) &&
                ((cur.storeType === 'CROSS' && storeType === 'cross') ||
                  (cur.storeType === 'LOCAL' && storeType !== 'cross'))
              ) {
                return {
                  min: cur.min,
                  max: cur.max,
                };
              }
              return res;
            }, undefined) || transitDaysDefaultValue.range,
        }
    : {
        ...transitDaysDefaultValue,
        storeScopes,
      };
};

export const getShipPlaceDefaultValues = (
  region: General.UpperCaseRegion,
  storeType: 'local' | 'cross' | 'pf',
  storeScopes: General.StoreScope[],
  defaultValueRule?: BootGuide.AiModel.DefaultValueRule,
): BootGuide.AiModel.FormValues.ShipPlace => {
  const isAreaRegion =
    get(
      defaultValueRule,
      `LOGISTICS_SEND_OUT_PLACE.SEND_OUT_PLACE_OF_AREA.regionAreaMaps.${region}.${storeType}.type`,
    ) === 'AREA';
  return isAreaRegion
    ? {
        storeScopes,
        valueType: 'area',
        areas: [
          get(
            defaultValueRule,
            `LOGISTICS_SEND_OUT_PLACE.SEND_OUT_PLACE_OF_AREA.regionAreaMaps.${region}.${storeType}.defaultArea`,
            '',
          ),
        ],
        customAreas: [],
      }
    : {
        storeScopes,
        valueType: 'nearBy',
        areas: [],
        customAreas: [],
      };
};

const getElementId = (formIndex: number | string, storeGroupId: number | string) =>
  `storeGroupId-${formIndex}-${storeGroupId}`;

function Logistics() {
  const {
    t,
    defaultValueRule,
    defaultValueRuleLoading,
    initializing,
    editingValues,
    setEditingValues,
    editingValuesRef,
    stores,
    storeMap,
    storeScopesModal,
    showStoreScopesModal,
    editModal,
    showEditModal,
    nextStep,
    withPanel,
  } = useViewState<FV[]>();

  const { firstEditingValue, restEditingValue } = useMemo(() => {
    const [firstEditingValue, restEditingValue] = editingValues || [];
    return {
      firstEditingValue,
      restEditingValue,
    };
  }, [editingValues]);

  const {
    loading: storeGroupListLoading,
    data: [storeGroupList = undefined, knowledgesList] = [],
  } = useRequest(() => {
    return Promise.all([
      getStoreGroupList().then((res) => storeGroupList2withScopes(res, storeMap)),
      Promise.all(knowledgeTypes.map(getCommonTransferedKnowledges)),
    ]).catch(() => {
      message.error(t('phrase.获取数据失败，请稍后再试'));
    });
  });

  const getKnowledgeValues = useGetKnowledgeValues();
  const { getInitialValues: getKnowledgeInitialValues } = useInitialValues();

  const getInitialValues = useCallback(
    (
      stores: UnConfigStore[],
      storeGroupList?: StoreGroupWithScopes[],
      knowledgesList?: any[][],
    ): FV => {
      if (!storeGroupList) {
        return [];
      }
      const storeMap = getStoreMap(stores);
      const getStepInitialValues = (
        stepType: 'AI_MODEL_SHIPPING_DAYS' | 'AI_MODEL_TRANSIT_DAYS' | 'AI_MODEL_SHIP_PLACE',
      ) => {
        const type = stepKnowledgeTypeMap[stepType];
        const { knowledgeValues } = getKnowledgeValues({
          type,
          unConfigStores: stores,
          defaultValueRule,
        });
        return getKnowledgeInitialValues({
          type,
          storeGroupList,
          storeMap,
          knowledgeValues,
        }).reduce<Record<number, any[]>>((res, { storeGroup, knowledges }) => {
          res[storeGroup.id] = knowledges.map(({ adaptValue }: any) => adaptValue);
          return res;
        }, {});
      };
      const knowledgesMapWithExist = (
        type: Knowledge.KnowledgeType,
        knowledgesMap: Record<number, any[]>,
        existKnowledges?: any[],
      ) => {
        return getKnowledgeInitialValues({
          type,
          storeGroupList,
          storeMap,
          knowledgeValues: existKnowledges,
        }).reduce((res, cur) => {
          if (!cur.knowledges.length) {
            return res;
          }
          const existKnowledges = (cur.knowledges as any[]).filter(({ condition }) => {
            const { dtScope, itemScope, tradeStatusScope } = condition;
            return (
              dtScope.filterCode === 'DT_ALL' &&
              itemScope.filterCode === 'ITEM_ALL' &&
              tradeStatusScope.filterCode === 'TRADE_STATUS_ALL'
            );
          });
          if (!existKnowledges.length) {
            return res;
          }
          const existStoreIds = existKnowledges.reduce<number[]>((res, cur) => {
            res.push(...storeScopes2storeIds(cur.condition.storeScope.storeScope));
            return res;
          }, []);
          const remainStoreMap = getStoreMap(
            stores.filter(({ id }) => !existStoreIds.includes(id)),
          );
          const knowledges = (res[cur.storeGroup.id] as any[]).reduce<any[]>((res, cur) => {
            const filteredStoreScopes = filterStoreScopes(
              type === 'LOGISTICS_SEND_OUT_TIME' ? cur.first.storeScopes : cur.storeScopes,
              remainStoreMap,
            );
            if (filteredStoreScopes.length) {
              res.push(
                type === 'LOGISTICS_SEND_OUT_TIME'
                  ? {
                      first: {
                        ...cur.first,
                        storeScopes: filteredStoreScopes,
                      },
                      preSale: cur.preSale
                        ? {
                            ...cur.preSale,
                            storeScopes: filteredStoreScopes,
                          }
                        : undefined,
                    }
                  : {
                      ...cur,
                      storeScopes: filteredStoreScopes,
                    },
              );
            }
            return res;
          }, []);
          res[cur.storeGroup.id] = existKnowledges
            .map(({ adaptValue }) => adaptValue)
            .reduce((res, cur) => {
              if (type !== 'LOGISTICS_SEND_OUT_TIME') {
                const { storeScopes, ...others } = cur;
                const targetIndex = knowledges.findIndex((target) => {
                  const { storeScopes, ...targetOthers } = target;
                  return isEqual(others, targetOthers);
                });
                if (targetIndex < 0) {
                  res.push(cur);
                  return res;
                }
                const target = res[targetIndex];
                const combinedStoreScopes = combineStoreScopes([storeScopes, target.storeScopes]);
                res[targetIndex] = {
                  ...others,
                  storeScopes: combinedStoreScopes,
                };
                return res;
              }
              const {
                first: { storeScopes, ...firstOthers },
                preSale: { storeScopes: _, ...preSaleOthers } = {},
              } = cur;
              const targetIndex = knowledges.findIndex((target) => {
                const {
                  first: { storeScopes, ...targetFirstOthers },
                  preSale: { storeScopes: _, ...targetPreSaleOthers } = {},
                } = target;
                return (
                  isEqual(firstOthers, targetFirstOthers) &&
                  isEqual(preSaleOthers, targetPreSaleOthers)
                );
              });
              if (targetIndex < 0) {
                res.push(cur);
                return res;
              }
              const target = res[targetIndex];
              const combinedStoreScopes = combineStoreScopes([
                storeScopes,
                target.first.storeScopes,
              ]);
              res[targetIndex] = {
                first: {
                  ...firstOthers,
                  storeScopes: combinedStoreScopes,
                },
                preSale: cur.preSale
                  ? {
                      ...preSaleOthers,
                      storeScopes: combinedStoreScopes,
                    }
                  : undefined,
              };
              return res;
            }, knowledges);
          return res;
        }, knowledgesMap);
      };
      const shippingDays = knowledgesMapWithExist(
        'LOGISTICS_SEND_OUT_TIME',
        getStepInitialValues('AI_MODEL_SHIPPING_DAYS'),
        knowledgesList?.[0],
      );
      const transitDays = knowledgesMapWithExist(
        'LOGISTICS_DELIVERED_IN_TIME',
        getStepInitialValues('AI_MODEL_TRANSIT_DAYS'),
        knowledgesList?.[2],
      );
      const shipPlace = knowledgesMapWithExist(
        'LOGISTICS_SEND_OUT_PLACE',
        getStepInitialValues('AI_MODEL_SHIP_PLACE'),
        knowledgesList?.[1],
      );
      const freeShippingExistValues = getKnowledgeInitialValues({
        type: 'SHOP_FREE_SHIPPING',
        storeGroupList,
        storeMap,
        knowledgeValues: knowledgesList?.[3],
      }).map(({ adaptValue }) => adaptValue);
      const specifyExpressTypeExistValues = getKnowledgeInitialValues({
        type: 'LOGISTICS_DELIVERY_COURIER_TYPE',
        storeGroupList,
        storeMap,
        knowledgeValues: knowledgesList?.[4],
      }).map(({ adaptValue }) => adaptValue);
      const combinedShippingExistValues = getKnowledgeInitialValues({
        type: 'LOGISTICS_CONSOLIDATED_MULTIPLE_ORDER',
        storeGroupList,
        storeMap,
        knowledgeValues: knowledgesList?.[5],
      }).map(({ adaptValue }) => adaptValue);
      const valuesWithExist = (
        values: any[],
        existValues: any[],
        storeGroup: StoreGroupWithScopes,
      ) => {
        const storeGroupStores = storeIds2stores(
          storeScopes2storeIds(storeGroup.storeScopes),
          storeMap,
        );
        const storeGroupExistValues = existValues
          .map(({ storeScopes, ...others }) => {
            return {
              ...others,
              storeScopes: filterStoreScopes(storeScopes, getStoreMap(storeGroupStores)),
            };
          })
          .filter(({ storeScopes }) => storeScopes.length);
        const existStoreIds = storeGroupExistValues.reduce<number[]>((res, { storeScopes }) => {
          res.push(...storeScopes2storeIds(storeScopes));
          return res;
        }, []);
        const remainStores = storeGroupStores.filter(({ id }) => !existStoreIds.includes(id));
        const res = storeGroupExistValues.reduce<any[]>(
          (res, value) => {
            const { storeScopes, ...others } = value;
            const targetIndex = res.findIndex((target) => {
              const { storeScopes, ...targetOthers } = target;
              return isEqual(others, targetOthers);
            });
            if (targetIndex < 0) {
              res.push(value);
              return res;
            }
            res[targetIndex] = {
              ...value,
              storeScopes: combineStoreScopes([storeScopes, res[targetIndex].storeScopes]),
            };
            return res;
          },
          values
            .map(({ storeScopes, ...others }) => {
              return {
                ...others,
                storeScopes: filterStoreScopes(storeScopes, getStoreMap(remainStores)),
              };
            })
            .filter(({ storeScopes }) => storeScopes.length),
        );
        return res;
      };
      return storeGroupList.map((storeGroup) => {
        const { id, storeScopes, storeIds } = storeGroup;
        const regionTypeStoresMap = getRegionTypeStoresMap(storeIds2stores(storeIds, storeMap));
        return {
          storeGroup,
          AI_MODEL_SHIPPING_DAYS: shippingDays[id],
          AI_MODEL_TRANSIT_DAYS: transitDays[id],
          AI_MODEL_SHIP_PLACE: shipPlace[id],
          AI_MODEL_FREE_SHIPPING: valuesWithExist(
            Object.keys(regionTypeStoresMap).reduce<any[]>((res, region) => {
              const typeStoresMap = regionTypeStoresMap[region];
              if (!typeStoresMap) {
                return res;
              }
              return Object.keys(typeStoresMap).reduce((res, storeType) => {
                const stores = typeStoresMap[storeType];
                if (!stores?.length) {
                  return res;
                }
                const storeScopes = stores2storeScopes(stores);
                res.push({
                  storeScopes,
                  freeShippingType: (
                    {
                      '3pf': 'FREE_SHIPPING_BY_PLATFORM_SEND_COUPON',
                      cross: 'NO_FREE_SHIPPING',
                      local: 'FREE_SHIPPING_BY_PLATFORM_SEND_COUPON',
                    } as const
                  )[storeType],
                });
                return res;
              }, res);
            }, []),
            freeShippingExistValues,
            storeGroup,
          ),
          AI_MODEL_SPECIFY_EXPRESS_TYPE: valuesWithExist(
            [{ value: 'noSupport', storeScopes, deliveryTypes: [] }],
            specifyExpressTypeExistValues,
            storeGroup,
          ),
          AI_MODEL_COMBINED_SHIPPING: valuesWithExist(
            [{ value: 'noSupport', storeScopes }],
            combinedShippingExistValues,
            storeGroup,
          ),
        };
      });
    },
    [defaultValueRule, getKnowledgeInitialValues, getKnowledgeValues],
  );

  const initialValues = useMemo(() => {
    if (firstEditingValue) {
      return firstEditingValue;
    }

    return getInitialValues(stores, storeGroupList, knowledgesList);
  }, [firstEditingValue, getInitialValues, stores, storeGroupList, knowledgesList]);

  const [hasStoresGroupList, setHasStoresGroupList] = useState<StoreGroupWithScopes[]>([]);

  useEffect(() => {
    setHasStoresGroupList(
      initialValues
        .map(({ storeGroup }) => storeGroup)
        .filter(({ storeScopes }) => storeScopes.length),
    );
  }, [initialValues]);

  const getRemainStores = useCallback((stores: UnConfigStore[], values: FV) => {
    return stores.filter((store) => {
      return values.every(({ storeGroup: { storeScopes } }) => {
        return !storeScopesIncludeStore(storeScopes, store);
      });
    });
  }, []);

  const storeGroupModalState = useModal<{
    storeGroup: StoreGroupWithScopes;
    onSubmit: Parameters<typeof StoreGroupModal>[0]['onSubmit'];
  }>();
  const storeGroupModal = storeGroupModalState.meta && (
    <StoreGroupModal
      visible={storeGroupModalState.visible}
      onCancel={() => storeGroupModalState.hide()}
      storeGroupWithScopes={storeGroupModalState.meta.storeGroup}
      stores={stores}
      storeMap={storeMap}
      onSubmit={storeGroupModalState.meta.onSubmit}
    />
  );

  const { getStoreGroupName } = useGetStoreGroupName();

  const ref = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<string>();
  const tabs = (
    <Tabs
      size="small"
      tabPosition="right"
      activeKey={activeTab}
      onChange={(key) => {
        const [formIndex, groupId] = key.split('-');
        const element = document.getElementById(getElementId(formIndex, groupId));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }}
    >
      {(editingValues
        ? editingValues.map((editingValue) => editingValue.map(({ storeGroup }) => storeGroup))
        : [hasStoresGroupList]
      ).map((storeGroups, formIndex) => {
        return storeGroups.map((storeGroup) => {
          const { storeScopes, id } = storeGroup;
          return storeScopes.length ? (
            <Tabs.TabPane key={`${formIndex}-${id}`} tab={getStoreGroupName(storeGroup)} />
          ) : null;
        });
      })}
    </Tabs>
  );
  useEffect(() => {
    const scrollElement = ref.current?.parentElement?.parentElement?.parentElement?.parentElement;

    if (!scrollElement) {
      return;
    }

    const scrollHandler = () => {
      const target = (
        editingValues
          ? editingValues
              .map((editingValue, formIndex) =>
                editingValue.map(({ storeGroup }) => ({
                  ...storeGroup,
                  formIndex,
                })),
              )
              .flat()
          : hasStoresGroupList.map((storeGroup) => ({
              ...storeGroup,
              formIndex: 0,
            }))
      ).find(({ id, formIndex }) => {
        const element = document.getElementById(getElementId(formIndex, id));
        return (
          element &&
          element.offsetTop + element.clientHeight >
            scrollElement.offsetTop + scrollElement.scrollTop
        );
      });
      if (target) {
        setActiveTab(`${target.formIndex}-${target.id}`);
      }
    };

    scrollHandler();

    scrollElement.addEventListener('scroll', scrollHandler);

    return () => scrollElement.removeEventListener('scroll', scrollHandler);
  }, [editingValues, hasStoresGroupList]);

  if (defaultValueRuleLoading || initializing || storeGroupListLoading) {
    return withPanel(<LoadingOutlined />);
  }

  const formContentRender = ({ values, setValues }: FormikProps<FV>, formIndex: number) => {
    return values.map(({ storeGroup, ...others }, index) => {
      const { id: storeGroupId, storeScopes, isDefault } = storeGroup;
      const storeCount = getStoreCount(storeScopes);
      if (!storeCount) {
        return null;
      }
      return (
        <div id={getElementId(formIndex, storeGroupId)}>
          <FormField<FV[0]> name={`${index}`}>
            {({ field: { value, onChange } }) => {
              const itemRender = (
                title: string,
                name: keyof typeof others,
                Component: ElementType,
              ) => {
                const getEditButton = (index2: number) => (
                  <Button
                    type="link"
                    icon={<SwapOutlined />}
                    onClick={() => {
                      showEditModal({
                        key: `${formIndex}@${index}@${name}@${index2}`,
                        formikConfig: {
                          initialValues: value[name][index2],
                          onSubmit: (values) =>
                            onChange({
                              ...value,
                              [name]: [
                                ...value[name].slice(0, index2),
                                values,
                                ...value[name].slice(index2 + 1),
                              ],
                            }),
                        },
                        modalProps: {
                          title,
                          children: <Component storeMap={storeMap} />,
                        } as any,
                      });
                    }}
                  >
                    {t('选择其他选项\n')}
                  </Button>
                );
                return name !== 'AI_MODEL_SHIPPING_DAYS' ? (
                  <FormField<FV[0][typeof name]>
                    name={`${index}.${name}`}
                    validate={(value) => {
                      const remainStoreCount =
                        storeCount -
                        value.reduce((res, { storeScopes }) => res + getStoreCount(storeScopes), 0);
                      if (remainStoreCount > 0) {
                        return t('存在x家店铺未完成配置', { count: remainStoreCount });
                      }
                    }}
                  >
                    {({ field: { value, onChange } }) => {
                      return (
                        <section>
                          <header>
                            <h4>{title}</h4>
                            <Button
                              type="link"
                              icon={<PlusOutlined />}
                              onClick={() => {
                                const unConfigStores = storeIds2stores(
                                  storeScopes2storeIds(storeGroup.storeScopes),
                                  storeMap,
                                ).filter((store) => {
                                  return value.every(({ storeScopes }) => {
                                    return !storeScopesIncludeStore(storeScopes, store);
                                  });
                                });
                                onChange([
                                  ...value,
                                  {
                                    ...(defaultValueMap[name] as any),
                                    storeScopes: stores2storeScopes(unConfigStores),
                                  },
                                ]);
                              }}
                            >
                              {t('添加')}
                            </Button>
                          </header>
                          {value.map((cur, index2) => {
                            return (
                              <div>
                                <div>
                                  <header>
                                    <span>{t('适用店铺')}</span>
                                    <StoreSelect
                                      value={cur.storeScopes}
                                      storeMap={storeMap}
                                      onEdit={() => {
                                        const storeDataSource = storeIds2stores(
                                          storeScopes2storeIds(storeScopes),
                                          storeMap,
                                        );
                                        showStoreScopesModal({
                                          storeDataSource,
                                          value: cur.storeScopes,
                                          key: `${formIndex}@${index}@${index2}`,
                                          onChange: (storeScopes) => {
                                            onChange(
                                              uniqStoreScopes(
                                                removeStoreScopesAll(storeScopes, storeDataSource),
                                                value as any[],
                                                index2,
                                              ).filter(({ storeScopes }) =>
                                                getStoreCount(storeScopes),
                                              ),
                                            );
                                          },
                                        });
                                      }}
                                    />
                                  </header>
                                  <section>
                                    <Component
                                      singleMode
                                      prefix={`${index}.${name}.${index2}`}
                                      storeMap={storeMap}
                                    />
                                    <Divider type="vertical" />
                                    {getEditButton(index2)}
                                  </section>
                                </div>
                                <Button
                                  type="text"
                                  icon={<DeleteOutlined />}
                                  onClick={() => {
                                    onChange([
                                      ...value.slice(0, index2),
                                      ...value.slice(index2 + 1),
                                    ] as any);
                                  }}
                                >
                                  {t('phrase.删除')}
                                </Button>
                              </div>
                            );
                          })}
                        </section>
                      );
                    }}
                  </FormField>
                ) : (
                  <FormField<FV[0][typeof name]>
                    name={`${index}.${name}`}
                    validate={(value) => {
                      const remainStoreCount =
                        storeCount -
                        value.reduce(
                          (res, { first: { storeScopes } }) => res + getStoreCount(storeScopes),
                          0,
                        );
                      if (remainStoreCount > 0) {
                        return t('存在x家店铺未完成配置', { count: remainStoreCount });
                      }
                    }}
                  >
                    {({ field: { value, onChange } }) => {
                      return (
                        <section>
                          <header>
                            <h4>{title}</h4>
                            <Button
                              type="link"
                              icon={<PlusOutlined />}
                              onClick={() => {
                                const unConfigStores = storeIds2stores(
                                  storeScopes2storeIds(storeGroup.storeScopes),
                                  storeMap,
                                ).filter((store) => {
                                  return value.every(({ first: { storeScopes } }) => {
                                    return !storeScopesIncludeStore(storeScopes, store);
                                  });
                                });
                                onChange([
                                  ...value,
                                  {
                                    first: {
                                      ...defaultValueMap[name],
                                      storeScopes: stores2storeScopes(unConfigStores),
                                    },
                                  },
                                ]);
                              }}
                            >
                              {t('添加')}
                            </Button>
                          </header>
                          {value.map((cur, index2) => {
                            return (
                              <div>
                                <div>
                                  <header>
                                    <span>{t('适用店铺')}</span>
                                    <StoreSelect
                                      value={cur.first.storeScopes}
                                      storeMap={storeMap}
                                      onEdit={() => {
                                        const storeDataSource = storeIds2stores(
                                          storeScopes2storeIds(storeGroup.storeScopes),
                                          storeMap,
                                        );
                                        showStoreScopesModal({
                                          storeDataSource,
                                          value: cur.first.storeScopes,
                                          key: `${formIndex}@${index}@${index2}`,
                                          onChange: (storeScopes) => {
                                            onChange(
                                              uniqStoreScopes(
                                                removeStoreScopesAll(storeScopes, storeDataSource),
                                                value.map(({ first }) => first),
                                                index2,
                                              )
                                                .map((first, index) => ({
                                                  first,
                                                  preSale: value[index].preSale
                                                    ? {
                                                        ...value[index].preSale,
                                                        storeScopes: first.storeScopes,
                                                      }
                                                    : undefined,
                                                }))
                                                .filter(({ first: { storeScopes } }) =>
                                                  getStoreCount(storeScopes),
                                                ),
                                            );
                                          },
                                        });
                                      }}
                                    />
                                  </header>
                                  <MultipleShippingDays
                                    singleMode
                                    prefix={`${index}.${name}.${index2}`}
                                    render={(first, preSale) => {
                                      return (
                                        <section>
                                          <ShippingDaysContent>
                                            <Inline>{first}</Inline>
                                            {preSale && <Inline>{preSale}</Inline>}
                                          </ShippingDaysContent>
                                          <Divider type="vertical" />
                                          {getEditButton(index2)}
                                        </section>
                                      );
                                    }}
                                  />
                                </div>
                                <Button
                                  type="text"
                                  icon={<DeleteOutlined />}
                                  onClick={() => {
                                    onChange([
                                      ...value.slice(0, index2),
                                      ...value.slice(index2 + 1),
                                    ]);
                                  }}
                                >
                                  {t('phrase.删除')}
                                </Button>
                              </div>
                            );
                          })}
                        </section>
                      );
                    }}
                  </FormField>
                );
              };
              return (
                <Wrapper>
                  <header>
                    {getStoreGroupName(storeGroup)}(
                    {t('ViewBlockList.{x}家店铺', { count: storeCount })})
                    <Preview
                      content={
                        <PreviewWrapper>
                          <StoreScopesPreview
                            value={storeScopes}
                            showSearch={false}
                            showTitle={false}
                            storeMap={storeMap}
                          />
                        </PreviewWrapper>
                      }
                    />
                    {!isDefault && (
                      <Button
                        type="link"
                        onClick={() =>
                          storeGroupModalState.show({
                            storeGroup: {
                              ...storeGroup,
                              name: getStoreGroupName(storeGroup),
                            },
                            onSubmit: (storeGroupFormValues) => {
                              const { id, name, storeScopes } = storeGroupFormValues;
                              if (
                                storeGroupList?.some(
                                  (storeGroup) => storeGroup.id !== id && storeGroup.name === name,
                                )
                              ) {
                                message.warn(t('与已有配置组名称一致'));
                                return Promise.resolve();
                              }
                              if (isEqual(storeScopes, storeGroup.storeScopes)) {
                                if (name !== getStoreGroupName(storeGroup)) {
                                  const nextStoreGroup = {
                                    ...storeGroup,
                                    name,
                                  };
                                  onChange({
                                    ...value,
                                    storeGroup: nextStoreGroup,
                                  });
                                  setHasStoresGroupList(
                                    hasStoresGroupList.map((storeGroup) =>
                                      storeGroup.id === nextStoreGroup.id
                                        ? nextStoreGroup
                                        : storeGroup,
                                    ),
                                  );
                                }
                                storeGroupModalState.hide();
                                return Promise.resolve();
                              }
                              if (restEditingValue) {
                                setEditingValues([firstEditingValue]);
                              }
                              const nextStoreScopes = uniqStoreScopes(
                                storeScopes,
                                values.map(({ storeGroup }) => storeGroup),
                                index,
                              ).map(({ storeScopes, storeIds, ...others }) => {
                                return {
                                  ...others,
                                  storeScopes,
                                  storeIds: storeScopes2storeIds(storeScopes),
                                };
                              });
                              const nextValues = values.map(({ storeGroup, ...others }, index2) => {
                                const nextStoreGroup =
                                  index2 === index
                                    ? {
                                        ...nextStoreScopes[index2],
                                        name,
                                      }
                                    : nextStoreScopes[index2];
                                const { storeScopes: preStoreScopes } = storeGroup;
                                const { storeScopes } = nextStoreGroup;
                                const reducedStoreIds = getStoreScopesReducedStoreIds(
                                  preStoreScopes,
                                  storeScopes,
                                );
                                const addedStoreIds = getStoreScopesReducedStoreIds(
                                  storeScopes,
                                  preStoreScopes,
                                );
                                if (reducedStoreIds.length) {
                                  Object.keys(others).forEach((key) => {
                                    if (key === 'AI_MODEL_SHIPPING_DAYS') {
                                      others[key] = others[key].map(({ first, preSale }) => {
                                        const storeScopes = storeScopesRemoveStoreIds(
                                          first.storeScopes,
                                          reducedStoreIds,
                                        );
                                        return {
                                          first: {
                                            ...first,
                                            storeScopes,
                                          },
                                          preSale: preSale && {
                                            ...preSale,
                                            storeScopes,
                                          },
                                        };
                                      });
                                    } else {
                                      others[key] = others[key].map<any>(
                                        ({ storeScopes, ...others }) => {
                                          return {
                                            ...others,
                                            storeScopes: storeScopesRemoveStoreIds(
                                              storeScopes,
                                              reducedStoreIds,
                                            ),
                                          };
                                        },
                                      );
                                    }
                                  });
                                }
                                if (addedStoreIds.length) {
                                  const addedValues = getInitialValues(
                                    storeIds2stores(addedStoreIds, storeMap),
                                    nextStoreScopes,
                                    knowledgesList,
                                  ).find(({ storeGroup: { id } }) => id === storeGroup.id);
                                  if (addedValues) {
                                    Object.keys(others).forEach((key) => {
                                      if (key === 'AI_MODEL_SHIPPING_DAYS') {
                                        others[key] = addedValues[key].reduce(
                                          (res, addedValue) => {
                                            const {
                                              first: { storeScopes, ...firstOthers },
                                              preSale: {
                                                storeScopes: preSaleStoreScopes,
                                                ...preSaleOthers
                                              } = {},
                                            } = addedValue;
                                            const targetIndex = res.findIndex((item) => {
                                              const {
                                                first: { storeScopes: _, ...firstOthers2 },
                                                preSale: {
                                                  storeScopes: __,
                                                  ...preSaleOthers2
                                                } = {},
                                              } = item;
                                              return (
                                                isEqual(firstOthers, firstOthers2) &&
                                                isEqual(preSaleOthers, preSaleOthers2)
                                              );
                                            });
                                            if (targetIndex < 0) {
                                              res.push(addedValue);
                                            } else {
                                              const nextStoreScopes = combineStoreScopes([
                                                res[targetIndex].first.storeScopes,
                                                storeScopes,
                                              ]);
                                              res[targetIndex] = {
                                                first: {
                                                  ...res[targetIndex].first,
                                                  storeScopes: nextStoreScopes,
                                                },
                                                preSale: res[targetIndex].preSale && {
                                                  ...res[targetIndex].preSale,
                                                  storeScopes: nextStoreScopes,
                                                },
                                              };
                                            }
                                            return res;
                                          },
                                          [...others[key]],
                                        );
                                      } else {
                                        others[key] = addedValues[key].reduce<any[]>(
                                          (res, addedValue) => {
                                            const { storeScopes, ...others } = addedValue;
                                            const targetIndex = res.findIndex(
                                              ({ storeScopes, ...others2 }) => {
                                                return isEqual(others, others2);
                                              },
                                            );
                                            if (targetIndex < 0) {
                                              res.push(addedValue);
                                            } else {
                                              const nextStoreScopes = combineStoreScopes([
                                                res[targetIndex].storeScopes,
                                                storeScopes,
                                              ]);
                                              res[targetIndex] = {
                                                ...others,
                                                storeScopes: nextStoreScopes,
                                              };
                                            }
                                            return res;
                                          },
                                          [...others[key]],
                                        );
                                      }
                                    });
                                  }
                                }
                                return {
                                  ...others,
                                  storeGroup: nextStoreGroup,
                                };
                              });
                              setValues(nextValues);
                              setHasStoresGroupList(
                                nextValues
                                  .map(({ storeGroup }) => storeGroup)
                                  .filter(({ storeScopes }) => storeScopes.length),
                              );
                              storeGroupModalState.hide();
                              return Promise.resolve();
                            },
                          })
                        }
                      >
                        {t('编辑配置组')}
                      </Button>
                    )}
                  </header>
                  {itemRender(t('发货天数'), 'AI_MODEL_SHIPPING_DAYS', MultipleShippingDays)}
                  {itemRender(t('发货地'), 'AI_MODEL_SHIP_PLACE', ShipPlace)}
                  {itemRender(t('运输天数'), 'AI_MODEL_TRANSIT_DAYS', TransitDays)}
                  {itemRender(t('是否包邮'), 'AI_MODEL_FREE_SHIPPING', FreeShipping)}
                  {itemRender(
                    t('是否支持买家指定快递种类'),
                    'AI_MODEL_SPECIFY_EXPRESS_TYPE',
                    SpecifyExpressType,
                  )}
                  {itemRender(
                    t('是否支持多个订单合并发货'),
                    'AI_MODEL_COMBINED_SHIPPING',
                    CombinedShipping,
                  )}
                </Wrapper>
              );
            }}
          </FormField>
        </div>
      );
    });
  };

  const values2Knowledges = (values: FV) => {
    return values.reduce<any[]>((res, cur) => {
      if (getStoreCount(cur.storeGroup.storeScopes) > 0) {
        const { storeGroup, AI_MODEL_SHIPPING_DAYS, ...others } = cur;
        AI_MODEL_SHIPPING_DAYS.forEach(({ first, preSale }) => {
          if (first.storeScopes.length) {
            res.push(...formValueMap2knowledges({ AI_MODEL_SHIPPING_DAYS: first }));
            if (preSale) {
              res.push(...formValueMap2knowledges({ AI_MODEL_SHIPPING_DAYS: preSale }));
            }
          }
        });
        Object.keys(others).forEach((key) => {
          others[key].forEach((value) => {
            if (value.storeScopes.length) {
              res.push(...formValueMap2knowledges({ [key]: value }));
            }
          });
        });
      }
      return res;
    }, []);
  };

  const onSave = (editingValues: FV[]) => {
    setEditingValues(editingValues);
    editingValuesRef.current = editingValues;
    nextStep();
    updateKnowledgeStatus({
      definitionCodes: knowledgeTypes,
      status: true,
    }).catch(() => {});
    const [firstEditingValue, restEditingValue] = editingValues;
    const firstDefault = firstEditingValue.find(({ storeGroup: { isDefault } }) => isDefault);
    const restDefault = restEditingValue?.find(({ storeGroup: { isDefault } }) => isDefault);
    const defaultStoreScopes = combineStoreScopes([
      firstDefault?.storeGroup.storeScopes || [],
      restDefault?.storeGroup.storeScopes || [],
    ]);
    const defaultGroup = firstDefault
      ? {
          ...firstDefault.storeGroup,
          storeScopes: defaultStoreScopes,
          storeIds: storeScopes2storeIds(defaultStoreScopes),
        }
      : undefined;
    updateStoreGroupListBootGuide({
      storeGroupList: [
        ...firstEditingValue
          .map(({ storeGroup: { storeScopes, ...others } }) => others)
          .filter(({ isDefault }) => !isDefault),
        ...(defaultGroup ? [defaultGroup] : []),
      ],
    }).catch(() => {});
  };

  const firstForm = (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={(values) => {
        const remainStores = getRemainStores(stores, values);
        if (remainStores.length) {
          setEditingValues([
            values,
            getInitialValues(
              remainStores,
              storeGroupList?.map(({ isDefault, id, ...others }) => {
                return {
                  id,
                  isDefault,
                  ...others,
                  storeIds: isDefault ? remainStores.map(({ id }) => id) : [],
                  storeScopes: isDefault ? stores2storeScopes(remainStores) : [],
                };
              }),
            ),
          ]);
          return Promise.resolve();
        }
        const dirty = !isEqual(initialValues, values);
        posthog.capture('$bootGuideStepDirty', {
          stepName: 'AI_MODEL_LOGISTICS',
          stepText: stepTextMap.AI_MODEL_LOGISTICS,
          dirty,
        });
        const knowledges = values2Knowledges(values);
        return knowledgeRecreate({ knowledges })
          .then(() => {
            onSave([values]);
          })
          .catch(() => {
            message.error(t('phrase.保存失败，请稍后再试'));
          });
      }}
    >
      {(formProps) => {
        const { values, dirty, isSubmitting, handleSubmit } = formProps;
        if (!restEditingValue) {
          editingValuesRef.current = [values];
        }

        const remainStores = getRemainStores(stores, values);

        return (
          <Container>
            <p>{t('guide_logistics_tips')}</p>
            {formContentRender(formProps, 0)}
            <Footer>
              <Button
                type="primary"
                loading={isSubmitting}
                disabled={remainStores.length === 0 || !editingValues?.length ? false : !dirty}
                onClick={() => handleSubmit()}
              >
                {remainStores.length > 0
                  ? t('为其他x家店铺配置', { x: remainStores.length })
                  : t('确认，继续下一项')}
              </Button>
            </Footer>
          </Container>
        );
      }}
    </Formik>
  );

  const restForm = restEditingValue && (
    <Formik
      enableReinitialize
      initialValues={restEditingValue}
      onSubmit={(values) => {
        posthog.capture('$bootGuideStepDirty', {
          stepName: 'AI_MODEL_LOGISTICS',
          stepText: stepTextMap.AI_MODEL_LOGISTICS,
          dirty: true,
        });
        const currentEditingValues = [firstEditingValue, values];
        const knowledges = values2Knowledges(currentEditingValues.flat());
        return knowledgeRecreate({ knowledges })
          .then(() => {
            onSave(currentEditingValues);
          })
          .catch(() => {
            message.error(t('phrase.保存失败，请稍后再试'));
          });
      }}
    >
      {(formProps) => {
        const { values, isSubmitting, handleSubmit } = formProps;

        editingValuesRef.current = [firstEditingValue, values];

        return (
          <Container>
            {formContentRender(formProps, 1)}
            <Footer>
              <Button type="primary" loading={isSubmitting} onClick={() => handleSubmit()}>
                {t('确认，继续下一项')}
              </Button>
            </Footer>
          </Container>
        );
      }}
    </Formik>
  );

  return withPanel(
    <Content ref={ref}>
      <section>
        {firstForm}
        {restForm && <Divider />}
        {restForm && <ScrollTo />}
        {restForm}
      </section>
      {tabs}
    </Content>,
    storeScopesModal,
    editModal,
    storeGroupModal,
  );
}

export const StoreDescription = memo(
  ({
    storeScopes,
    storeMap,
    divider,
  }: {
    storeScopes: General.StoreScope[];
    storeMap: ViewState['storeMap'];
    divider?: string;
  }) => {
    const { t } = useTranslate();

    const lang = useSystemLanguageValue();

    type GroupedStores = Partial<
      Record<'pf' | 'local' | 'cross', Partial<Record<General.UpperCaseRegion, UnConfigStore[]>>>
    >;

    const groupedStores = storeScopes.reduce<GroupedStores>((res, { regionStoreScopes }) => {
      return regionStoreScopes.reduce((res, { region, storeIds }) => {
        if (region === 'all' || !storeIds) {
          return res;
        }
        return storeIds.reduce((res, id) => {
          const store = storeMap[id];
          if (!store) {
            return res;
          }
          const groupStore = (type: 'pf' | 'local' | 'cross') => {
            if (!res[type]) {
              res[type] = {
                [store.region]: [store],
              };
            } else {
              if (!res[type][store.region]) {
                res[type][store.region] = [store];
              } else {
                res[type][store.region]?.push(store);
              }
            }
          };
          if (store.is3pf) {
            groupStore('pf');
          } else if (store.storeType === 'CROSS') {
            groupStore('cross');
          } else if (store.storeType === 'LOCAL') {
            groupStore('local');
          }
          return res;
        }, res);
      }, res);
    }, {});

    const tags = Object.keys(groupedStores).map((storeType) => {
      const regionMap = groupedStores[storeType] || {};
      return Object.keys(regionMap).map((region) => {
        const stores = regionMap[region] || [];
        if (stores.length > 0) {
          return (
            <Tag
              color={
                {
                  local: 'green',
                  cross: 'blue',
                  pf: 'orange',
                }[storeType]
              }
              className="!my-[2px]"
            >{`${t(`region.${toLowerCase(region)}`)}${divider || (lang === 'zh-CN' ? '' : ' ')}${
              {
                local: t('本土'),
                cross: t('跨境'),
                pf: '3PF',
              }[storeType]
            }`}</Tag>
          );
        }
        return null;
      });
    });

    return <>{tags}</>;
  },
);

const Content = styled.div`
  display: flex;
  align-items: flex-start;
  > .ant-tabs {
    flex: 0 0 auto;
    position: sticky;
    top: 0;
    margin-top: 32px;
  }
  > section {
    flex: 1 0 0;
    overflow: hidden;
  }
`;

const Container = styled.div`
  > p {
    margin-bottom: 8px;
  }
  > div {
    &:last-of-type {
      margin-bottom: 4px;
    }
    > .ant-form-item {
      padding: 12px 16px;
      background: #fff;
    }
  }
`;

const Wrapper = styled.div`
  > header {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
  }
  .ant-form-item-control-input-content > section {
    border: solid 1px #f0f2f5;
    > header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 40px;
      padding: 0 16px;
      > h4 {
        margin-bottom: 0;
      }
      > button {
        padding-right: 0;
      }
    }
    > div {
      border-top: solid 1px #f0f2f5;
      display: flex;
      padding: 8px 16px 0;
      > div {
        flex: 1 0 0;
        > header {
          display: flex;
          align-items: center;
          margin-bottom: 4px;
          > span {
            margin-right: 8px;
          }
        }
        > section {
          display: flex;
          > .ant-divider {
            margin: 10px 0 10px 10px;
          }
        }
      }
      > button {
        flex: 0 0 auto;
        padding-right: 0;
      }
    }
  }
`;

const ShippingDaysContent = styled.div`
  flex-direction: column;
  > .ant-divider {
    margin-top: 0 !important;
  }
  > span {
    margin-bottom: 8px;
    font-size: 12px;
    color: #84868c;
  }
  > div {
    align-items: flex-start;
    > .ant-divider {
      margin: 10px 2px 10px 8px;
    }
  }
`;

export default Logistics;
